import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth, API } from '../App';

/**
 * Hook to fetch and manage user context for auto-filling tools
 * Provides all user data (vision, profile, audit, etc.) in one place
 */
export const useUserContext = () => {
  const { token } = useAuth();
  const [context, setContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchContext = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await axios.get(`${API}/user-context`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContext(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching user context:', err);
      setError(err);
      // Still set empty context to avoid blocking
      setContext({ has_data: false, sources: [], data: {} });
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchContext();
  }, [fetchContext]);

  // Helper to get specific data
  const getVision = () => context?.data?.vision || null;
  const getProfile = () => context?.data?.profile || null;
  const getAudit = () => context?.data?.audit || null;
  const getArtistic = () => context?.data?.artistic || null;
  const getContent = () => context?.data?.content || null;
  const getExpectations = () => context?.data?.expectations || null;
  const getIdentityCard = () => context?.data?.identity_card || null;
  const getGlowUp = () => context?.data?.glow_up || null;

  // Get brand name from any available source
  const getBrandName = () => {
    return context?.data?.vision?.brand_name 
      || context?.data?.audit?.brand_name 
      || context?.data?.identity_card?.brand_name 
      || context?.user_name 
      || null;
  };

  // Get color palette from any available source
  const getColorPalette = () => {
    return context?.data?.vision?.color_palette 
      || context?.data?.artistic?.color_palette 
      || null;
  };

  // Get target audience from any available source
  const getTargetAudience = () => {
    return context?.data?.vision?.target_audience 
      || context?.data?.audit?.target_audience 
      || context?.data?.profile?.target_client 
      || null;
  };

  // Get brand voice/tone from any available source
  const getBrandVoice = () => {
    return context?.data?.profile?.brand_voice 
      || context?.data?.profile?.tone_of_voice 
      || null;
  };

  // Get unique positioning
  const getPositioning = () => {
    return context?.data?.profile?.unique_positioning 
      || context?.data?.audit?.current_positioning 
      || context?.data?.vision?.unique_value_proposition 
      || null;
  };

  // Check if specific data source exists
  const hasSource = (source) => context?.sources?.includes(source) || false;

  return {
    context,
    loading,
    error,
    refetch: fetchContext,
    hasData: context?.has_data || false,
    sources: context?.sources || [],
    sourcesDisplay: context?.sources_display || [],
    summaryMessage: context?.summary_message || null,
    // Data accessors
    getVision,
    getProfile,
    getAudit,
    getArtistic,
    getContent,
    getExpectations,
    getIdentityCard,
    getGlowUp,
    // Convenience accessors
    getBrandName,
    getColorPalette,
    getTargetAudience,
    getBrandVoice,
    getPositioning,
    hasSource
  };
};

export default useUserContext;
